import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, FileText, Inbox, Settings, Send, FileClock, ArrowRight, Plus } from "lucide-react";

const QuickAction = ({ to, label, description, icon: Icon, count, accent, navigate, testid }) => (
  <button
    onClick={() => navigate(to)}
    data-testid={testid}
    className="group bg-white border border-[#E2E4E9] hover:border-[#002FA7] hover:shadow-md rounded-sm p-5 md:p-6 text-left transition-all duration-150 active:scale-[0.99]"
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${accent || "bg-[#002FA7]/10 text-[#002FA7]"}`}>
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <ArrowRight className="w-4 h-4 text-[#5A5D6E] group-hover:text-[#002FA7] group-hover:translate-x-0.5 transition-all" />
    </div>
    <h2 className="font-display font-semibold text-lg leading-tight">{label}</h2>
    {description && <p className="text-xs text-[#5A5D6E] mt-1 leading-snug">{description}</p>}
    {count !== undefined && (
      <p className="font-mono text-2xl mt-3 text-[#0D0E15]">{count}</p>
    )}
  </button>
);

export default function Home() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => { api.stats().then(setS).catch(console.error); }, []);

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="mb-6 md:mb-8">
        <p className="text-[10px] text-[#5A5D6E] uppercase tracking-[0.2em] font-medium">Inicio</p>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#0D0E15] mt-2">¿Qué querés hacer?</h1>
        <p className="text-sm text-[#5A5D6E] mt-2">Accesos rápidos a las acciones más frecuentes.</p>
      </div>

      {/* Primary CTA */}
      <button
        onClick={() => navigate("/pedidos/nuevo")}
        data-testid="home-new-order-cta"
        className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white rounded-sm p-5 md:p-6 mb-6 flex items-center justify-between transition-colors group"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/15 flex items-center justify-center rounded-sm">
            <Send className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <div className="text-left">
            <h2 className="font-display font-bold text-lg md:text-xl">Nuevo pedido</h2>
            <p className="text-xs md:text-sm text-white/80 mt-0.5">Crear y enviar un pedido a un proveedor</p>
          </div>
        </div>
        <Plus className="w-6 h-6 group-hover:rotate-90 transition-transform duration-200" strokeWidth={1.75} />
      </button>

      {/* Quick access grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        <QuickAction
          to="/pedidos"
          label="Pedidos"
          description="Historial, borradores y reenvío"
          icon={Inbox}
          count={s?.orders_total}
          navigate={navigate}
          testid="home-card-orders"
        />
        <QuickAction
          to="/plantillas"
          label="Plantillas"
          description="Plantillas reutilizables por proveedor"
          icon={FileText}
          count={s?.templates}
          navigate={navigate}
          testid="home-card-templates"
        />
        <QuickAction
          to="/proveedores"
          label="Proveedores"
          description="Gestionar proveedores y prefijos"
          icon={Users}
          count={s?.suppliers}
          navigate={navigate}
          testid="home-card-suppliers"
        />
        {s?.orders_draft > 0 && (
          <QuickAction
            to="/pedidos"
            label="Borradores pendientes"
            description="Pedidos guardados sin enviar"
            icon={FileClock}
            count={s.orders_draft}
            accent="bg-[#F57F17]/10 text-[#F57F17]"
            navigate={navigate}
            testid="home-card-drafts"
          />
        )}
        <QuickAction
          to="/configuracion"
          label="Configuración"
          description="SMTP, branding y logo"
          icon={Settings}
          navigate={navigate}
          testid="home-card-config"
        />
      </div>
    </div>
  );
}
